import { type FC, useEffect, useState } from "react";
import NextLink from "next/link";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Link as MuiLink,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import {
  useDateFormat,
  usePlatform,
  useTranslation,
  useUser,
} from "@/shared/hooks";
import {
  UpdateProductPurchaseNotificationsMutation,
  usePastDueSubscription,
} from "@/entities/purchases";
import type {
  UpdateProductPurchaseNotificationsMutationType,
  UpdateProductPurchaseNotificationsMutationVariablesType,
} from "@/types/graphql/site.generated";
import { formatCurrency, getPeriodLabel } from "@/shared/utils";
import {
  ProductAccessErrorType,
  ProductTypeType,
} from "@/types/graphql/site.generated";
import type { Product, ProductPrice } from "@/entities/products";
import { PaymentTerms } from "./PaymentTerms";
import { isPaymentTermsRequired } from "./paymentTermsUrls";
import { PaymentDialog } from "./PaymentDialog";
import { PromocodeButton } from "./PromocodeButton";
import { subscriptionPriceLabels } from "./subscriptionPriceLabels";

// Карточка цены страницы продукта (макет 1b): подпись типа, цена крупно,
// бейдж «В продаже», табы периодов, промокод, плашка периода доступа,
// согласие с условиями и кнопка покупки. Клик по кнопке открывает
// PaymentDialog (Task 17) — Stripe Elements чекаут. Если у зрителя есть
// активная покупка (product.viewerPurchase), вместо всего этого показывается
// карточка «куплено» с периодом доступа.

// Порт ProductPayment.js:120-128 (getTitle) — подпись карточки по типу
// продукта; показывается и в обычном состоянии, и в ветке accessError
// (ProductPayment.js:217-232).
function getTypeTitle(
  t: ReturnType<typeof useTranslation>["t"],
  type: ProductTypeType,
): string {
  return type === ProductTypeType.SubscriptionType
    ? t("productPayment.subscription")
    : t("productPayment.ppvAccess");
}

function getAccessErrorText(
  t: ReturnType<typeof useTranslation>["t"],
  accessError: Product["accessError"],
  eventCancelled: boolean,
): string | null {
  // Отменённое событие: продажи закрыты навсегда, деньги возвращаются.
  if (eventCancelled) return t("product.cancelled");
  if (!accessError) return null;
  if (accessError === ProductAccessErrorType.NotAvailableForCountryType) {
    return t("product.accessError.notAvailableForCountry");
  }
  return t("product.accessError.salesFinished");
}

export const ProductPayment: FC<{
  product: Product;
  promoLoading: boolean;
  setPromoLoading: (value: boolean) => void;
  onPromocodeApplied: (promocode: { id: string }) => void;
  onPromocodeInvalid?: () => void;
  promoResetKey: number;
  /** Диалог покупки с экрана контента (useContentPurchase): узнать об
   * успешной оплате и о закрытии диалога оплаты. */
  onPaymentSucceeded?: () => void;
  onPaymentDialogClosed?: () => void;
}> = ({
  product,
  promoLoading,
  setPromoLoading,
  onPromocodeApplied,
  onPromocodeInvalid,
  promoResetKey,
  onPaymentSucceeded,
  onPaymentDialogClosed,
}) => {
  const { t } = useTranslation();
  const { formatDate, formatDateRange, formatDateTime } = useDateFormat();
  const { platform } = usePlatform();
  const { enqueueSnackbar } = useSnackbar();
  // Тумблер писем по покупке на карточке «куплено» (референс
  // ProductPurchase.js:197-201 → ToggleNotificationsProductPurchase): та же
  // мутация и те же тексты, что в «Моих покупках».
  const [updateNotifications, { loading: notificationsLoading }] = useMutation<
    UpdateProductPurchaseNotificationsMutationType,
    UpdateProductPurchaseNotificationsMutationVariablesType
  >(UpdateProductPurchaseNotificationsMutation, {
    onError: () => {
      enqueueSnackbar(t("purchases.notificationsToggle.error"), {
        variant: "error",
      });
    },
  });
  const [tab, setTab] = useState(0);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  // Цена, с которой открыли чекаут, запоминается на всё время диалога:
  // product.prices[].token переподписывается на каждый запрос продукта, а
  // после успешной оплаты цен может не остаться вовсе. Диалог не должен ни
  // перезаводить платёж по новому токену, ни исчезать с экраном «оплата
  // прошла» из-за рефетча.
  const [checkoutPrice, setCheckoutPrice] = useState<ProductPrice | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Гость не видит ошибку «необходима авторизация» от бэкенда: кнопка
  // открывает окно входа, запоминает цену и после входа сама продолжает
  // цепочку оплаты с того же места.
  const { user, openSignIn } = useUser();
  const [priceAfterSignIn, setPriceAfterSignIn] = useState<ProductPrice | null>(
    null,
  );
  // Подписка зрителя на этот продукт просрочена (PAST_DUE, спека
  // viewer-money): доступа нет, но вместо новой покупки зритель меняет карту,
  // и подписка продлевается сразу.
  const pastDueSubscription = usePastDueSubscription(product.id, {
    skip: !user || product.type !== ProductTypeType.SubscriptionType,
  });

  useEffect(() => {
    if (!user || !priceAfterSignIn) return;
    setCheckoutPrice(priceAfterSignIn);
    setPriceAfterSignIn(null);
    setPaymentDialogOpen(true);
  }, [user, priceAfterSignIn]);

  const startCheckout = (selected: ProductPrice) => {
    if (!user) {
      setPriceAfterSignIn(selected);
      openSignIn();
      return;
    }

    setCheckoutPrice(selected);
    setPaymentDialogOpen(true);
  };

  const typeTitle = getTypeTitle(t, product.type);
  const accessErrorText = getAccessErrorText(
    t,
    product.accessError,
    Boolean(product.eventCancelled),
  );
  const price: ProductPrice | undefined =
    product.prices[tab] ?? product.prices[0];

  // Диалог оплаты считается до ранних возвратов и рендерится и в ветке
  // «куплено»: рефетч после SUCCEEDED приносит viewerPurchase и переключает
  // карточку, а диалог обязан дожить до кнопки «Закрыть» с экраном «оплата
  // прошла» — иначе он исчезает из-под покупателя.
  const dialogPrice = checkoutPrice ?? price;
  const dialogLabels = dialogPrice
    ? subscriptionPriceLabels(t, product.type, dialogPrice)
    : null;
  const paymentDialog = dialogPrice ? (
    <PaymentDialog
      open={paymentDialogOpen}
      onClose={() => {
        setPaymentDialogOpen(false);
        setCheckoutPrice(null);
        onPaymentDialogClosed?.();
      }}
      productId={product.id}
      priceToken={dialogPrice.token}
      recurrent={product.type === ProductTypeType.SubscriptionType}
      providers={dialogPrice.providers}
      currency={dialogPrice.currency}
      productTitle={product.title}
      amountLabel={
        dialogLabels?.amountLabel ??
        formatCurrency(dialogPrice.value, dialogPrice.currency)
      }
      priceOnPeriod={dialogLabels?.priceOnPeriod ?? null}
      onPromocodeInvalid={onPromocodeInvalid}
      onSucceeded={onPaymentSucceeded}
    />
  ) : null;

  // Активная покупка зрителя (спека §4.1): ни цен, ни промокода, ни
  // условий — только период доступа и (для подписки) ссылка на «Мои
  // покупки», где выключается автопродление. Backend отдаёт только активную
  // покупку (без возврата и спора, expiredAt > now), но между ответом и рендером
  // в открытой вкладке проходит время, поэтому срок проверяется и здесь.
  const purchase = product.viewerPurchase;

  // Карточка считается в одном месте, а рендерится единственный раз ниже
  // вместе с диалогом оплаты: пока диалог жил по копии в каждой ветке, его
  // состояние (в том числе экран «оплата прошла») держалось только на том,
  // что копии стояли на одной позиции во фрагменте.
  const card = (() => {
    if (
      purchase &&
      !purchase.refunded &&
      !purchase.disputed &&
      new Date(purchase.expiredAt) > new Date()
    ) {
      return (
        <Card>
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography variant="label" sx={{ color: "text.muted" }}>
                  {typeTitle}
                </Typography>
                <Chip
                  variant="success"
                  label={t(
                    product.type === ProductTypeType.SubscriptionType
                      ? "productPayment.purchasedSubscription"
                      : "productPayment.purchasedPpvAccess",
                  )}
                />
              </Stack>
              <Typography variant="body1">
                {/* Продукт уже прошёл: период доступа покупателю не важен,
                легаси показывает дату окончания показа
                (ProductPurchase.js:56-60 → productCover.finished). */}
                {product.finished
                  ? t("product.finished", {
                      date: formatDateTime(product.finishAt),
                    })
                  : t("productPayment.accessPeriod", {
                      from: formatDateTime(purchase.startAt),
                      to: formatDateTime(purchase.expiredAt),
                    })}
              </Typography>
              <Tooltip
                title={t(
                  purchase.sendNotifications
                    ? "purchases.notificationsToggle.onTooltip"
                    : "purchases.notificationsToggle.offTooltip",
                )}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={purchase.sendNotifications}
                      disabled={notificationsLoading}
                      onChange={(event) =>
                        void updateNotifications({
                          variables: {
                            id: purchase.id,
                            sendNotifications: event.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label={t("purchases.notificationsToggle.label")}
                  sx={{ m: 0 }}
                  slotProps={{
                    typography: {
                      variant: "label",
                      sx: { color: "text.muted" },
                    },
                  }}
                />
              </Tooltip>
              {product.type === ProductTypeType.SubscriptionType && (
                <MuiLink
                  component={NextLink}
                  href="/purchases"
                  variant="body2"
                  underline="hover"
                  sx={{ color: "primary.light" }}
                >
                  {t("purchases.pageTitle")}
                </MuiLink>
              )}
            </Stack>
          </CardContent>
        </Card>
      );
    }

    if (pastDueSubscription) {
      return (
        <Card>
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            <Stack spacing={2}>
              <Typography variant="label" sx={{ color: "text.muted" }}>
                {typeTitle}
              </Typography>
              <Alert severity="warning">{t("purchases.pastDue.paywall")}</Alert>
              <Button
                fullWidth
                size="large"
                variant="contained"
                component={NextLink}
                href={
                  pastDueSubscription.cardUpdateAllowed
                    ? `/purchases?updateCard=${encodeURIComponent(
                        pastDueSubscription.id,
                      )}`
                    : "/purchases"
                }
              >
                {pastDueSubscription.cardUpdateAllowed
                  ? t("purchases.pastDue.paywallButton")
                  : t("purchases.pageTitle")}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      );
    }

    // Продукт с поштучной покупкой контента — вместо карточки цен рендерится
    // голая Card > CardContent с текстом-хинтом, без заголовка типа (референс:
    // ProductCover.js:153-155,183-191 — это отдельная ветка ProductCover,
    // а не ProductPayment).
    if (!accessErrorText && product.perContentPurchase) {
      return (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary">
              {t("product.perContentPurchaseHint")}
            </Typography>
          </CardContent>
        </Card>
      );
    }

    const showPrices = !accessErrorText && product.prices.length > 0;

    // Paynet compliance (Task 11): галочка PaymentTerms блокирует оплату,
    // только когда сама PaymentTerms есть на экране: оба url платформы заданы
    // и выбранная цена в MDL (уйдёт в Paynet). Условие вынесено в общий хелпер
    // isPaymentTermsRequired (paymentTermsUrls.ts) и используется отсюда же,
    // а не повторяется текстуально — иначе один рассинхрон между двумя копиями
    // условия молча вернул бы вечно недоступную кнопку оплаты без объяснения.
    const termsRequired = isPaymentTermsRequired(platform, price?.currency);

    // Порт ProductPriceButton.js:93-118: подпись кнопки при триале — только
    // для подписки, PPV триал не показывает.
    const isTrial =
      product.type === ProductTypeType.SubscriptionType &&
      !!price &&
      price.trialPeriod > 0;
    let trialButtonText: string | undefined;
    if (isTrial && price) {
      trialButtonText =
        price.trialValue === 0
          ? t("productPayment.trialButton", { count: price.trialPeriod })
          : t("productPayment.trialButtonCost", {
              count: price.trialPeriod,
              value: formatCurrency(price.trialValue, price.currency),
            });
    }

    return (
      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          <Stack spacing={2.5}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography
                  variant="label"
                  sx={{ color: "text.muted", mb: 0.75 }}
                >
                  {typeTitle}
                </Typography>
                {/* При accessError (продажи завершены, гео-ограничение) цена
                не показывается вовсе — в карточке остаются только подпись
                типа и чип с текстом ошибки, как было до редизайна. */}
                {showPrices && price ? (
                  <Stack direction="row" alignItems="baseline" spacing={1.5}>
                    <Typography variant="h3" component="div">
                      {formatCurrency(price.value, price.currency)}
                      {product.type === ProductTypeType.SubscriptionType && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.5em",
                            fontWeight: 600,
                            color: "text.secondary",
                            ml: 0.75,
                          }}
                        >
                          {`/ ${getPeriodLabel(t, product.type, price.period).toLowerCase()}`}
                        </Box>
                      )}
                    </Typography>
                    {price.oldValue ? (
                      <Typography
                        variant="body1"
                        sx={{
                          color: "text.muted",
                          textDecoration: "line-through",
                        }}
                      >
                        {formatCurrency(price.oldValue, price.currency)}
                      </Typography>
                    ) : null}
                  </Stack>
                ) : null}
              </Box>
              {accessErrorText ? (
                // У MUI собственный .MuiChip-label с nowrap/ellipsis,
                // поэтому перенос длинного текста («Недоступно в вашей
                // стране») задаётся именно на нём, а не на корне чипа.
                <Chip
                  variant="badgeSoft"
                  label={accessErrorText}
                  sx={{
                    height: "auto",
                    py: 0.5,
                    maxWidth: "100%",
                    flexShrink: 1,
                    "& .MuiChip-label": { whiteSpace: "normal" },
                  }}
                />
              ) : (
                showPrices && (
                  <Chip variant="success" label={t("product.onSale")} />
                )
              )}
            </Stack>

            {showPrices && price && product.prices.length > 1 && (
              <Tabs
                value={tab}
                onChange={(_event, value: number) => setTab(value)}
                variant="fullWidth"
              >
                {product.prices.map((p, index) => (
                  <Tab
                    key={p.id}
                    label={getPeriodLabel(t, product.type, p.period)}
                    value={index}
                  />
                ))}
              </Tabs>
            )}

            {showPrices && price && (
              <>
                <PromocodeButton
                  key={promoResetKey}
                  productId={product.id}
                  productType={product.type}
                  currency={price.currency}
                  onCompleted={onPromocodeApplied}
                  setLoading={setPromoLoading}
                />

                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="flex-start"
                  sx={(theme) => ({
                    p: "14px 16px",
                    borderRadius: "10px",
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  })}
                >
                  <AccessTimeIcon
                    sx={{ fontSize: 18, color: "primary.light", mt: "1px" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {/* У подписки диапазон дат первого периода ничего не
                    объясняет покупателю: важно, на сколько открывается
                    доступ и что он продлевается. У PPV диапазон как
                    раз и есть суть покупки. */}
                    {product.type === ProductTypeType.SubscriptionType
                      ? t("product.periodSubscriptionDays", {
                          count: price.period,
                        })
                      : t("product.ppvAccessPeriod", {
                          range: formatDateRange(
                            price.purchaseStartAt,
                            price.purchaseExpiredAt,
                          ),
                        })}
                  </Typography>
                </Stack>

                {price.trialPeriod > 0 &&
                  product.type === ProductTypeType.SubscriptionType && (
                    <Typography variant="body2" color="text.secondary">
                      {price.trialValue > 0
                        ? t("productPayment.trialDescCost", {
                            count: price.trialPeriod,
                            trialValue: formatCurrency(
                              price.trialValue,
                              price.currency,
                            ),
                            value: formatCurrency(price.value, price.currency),
                            per: getPeriodLabel(
                              t,
                              product.type,
                              price.period,
                            ).toLowerCase(),
                          })
                        : t("productPayment.trialDescFree", {
                            count: price.trialPeriod,
                            value: formatCurrency(price.value, price.currency),
                            per: getPeriodLabel(
                              t,
                              product.type,
                              price.period,
                            ).toLowerCase(),
                          })}
                    </Typography>
                  )}

                {price.nextPrice && price.nextPrice.value > price.value && (
                  <Typography variant="body2" color="text.secondary">
                    {t("product.nextPrice", {
                      date: formatDate(price.nextPrice.dateFrom),
                      value: formatCurrency(
                        price.nextPrice.value,
                        price.currency,
                      ),
                    })}
                  </Typography>
                )}

                <PaymentTerms
                  currency={price.currency}
                  checked={termsAccepted}
                  onChange={setTermsAccepted}
                />

                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={() => startCheckout(price)}
                  disabled={promoLoading || (termsRequired && !termsAccepted)}
                >
                  {trialButtonText ?? t("product.card.buy")}
                </Button>

                {/* caption мапится в <span>, поэтому align="center" без
                display:block не центрует строку преимуществ. */}
                <Typography
                  variant="caption"
                  align="center"
                  sx={{ display: "block", color: "text.muted" }}
                >
                  {t("product.paymentPerks")}
                </Typography>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  })();

  return (
    <>
      {card}
      {paymentDialog}
    </>
  );
};
